import type { IconName } from '@travelata-libs/ui-kit';
import type { Component } from 'vue';
import type {
  BeachLine,
  BeachType,
  ChildService,
  DistanceLift,
  HotelFacility,
  HotelProduct,
  RenovationType,
  RestType,
  WaterActivity,
} from '@/entities/hotel';
import type { Product, ProductSearch } from '@/entities/product';
import type { DestinationPrices, SearchCriteria, UseSearchCriteria } from '@/entities/searchCriteria';
import type { AccountUser } from '@/features/account';
import type { DepartureCity, FirstPaymentDefinitionsDTO } from '@/shared/model';
import type { SearchFormCriteria } from '@/widgets/searchForm';

export interface SerpDependencies {
  useSearchCriteria: UseSearchCriteria;
  productSearch: ProductSearch;
}

export interface SerpProduct {
  hotel: HotelProduct;
  products: Product[];
  cheapProduct: Product | null;
}

export type SerpCardDistance = 'airport' | 'beach' | 'center';

export interface SerpHotelOpenOptions {
  toReviews: boolean;
}

export type SearchProgressStage = 'operators' | 'hotels' | 'prices';

export type SerpSort = 'popular' | 'priceAsc' | 'priceDesc' | 'rating';

export type RatingFilterKey = 'best' | 'better' | 'good';

export enum FilterGroupId {
  IsExclusive = 'isExclusive',
  Budget = 'budget',
  BeachLine = 'beachLine',
  BeachType = 'beachType',
  BeachOwner = 'beachOwner',
  BonusMiles = 'bonusMiles',
  NearTheCenter = 'nearTheCenter',
  Rating = 'rating',
  Prepayments = 'prepayments',
  Facilities = 'facilities',
  WaterActivities = 'waterActivities',
  ChildServices = 'childServices',
  Renovation = 'renovation',
  DistanceLift = 'distanceLift',
  RestType = 'restType',
  Operators = 'operators',
}

export interface FiltersModel {
  isExclusive: boolean;
  budget: { min: number; max: number };
  beachLine: BeachLine[];
  beachType: BeachType[];
  beachOwner: boolean;
  bonusMiles: boolean;
  nearTheCenter: boolean;
  rating: RatingFilterKey | null;
  prepayments: string[];
  facilities: HotelFacility[];
  waterActivities: WaterActivity[];
  childServices: ChildService[];
  renovation: RenovationType[];
  distanceLift: DistanceLift[];
  restType: RestType | null;
  operators: string[];
}

export interface FiltersModelChange {
  previous: FiltersModel;
  filtersModel: FiltersModel;
  replaced: boolean;
}

export type SearchCriteriaFieldGroupId = Extract<keyof SearchCriteria, 'meals' | 'hotelCategories'>;

export type SearchCriteriaFlagGroupId = Extract<keyof SearchCriteria, 'directFlight' | 'block'>;

export type ResortsFilterGroupId = 'resorts';

export type SearchListFilterGroupId = SearchCriteriaFieldGroupId | ResortsFilterGroupId;

export type SearchFilterGroupId = SearchListFilterGroupId | SearchCriteriaFlagGroupId;

export type SearchCriteriaFilters = Pick<SearchCriteria, SearchCriteriaFieldGroupId | SearchCriteriaFlagGroupId> & {
  resorts?: number[];
};

export type SearchFiltersSource = Pick<SearchCriteria, 'destination'> &
  Partial<Pick<SearchCriteria, 'fromCity' | SearchCriteriaFieldGroupId | SearchCriteriaFlagGroupId>>;

export type SerpFilterGroupId = FilterGroupId | SearchFilterGroupId;

export interface SerpFilterOption {
  key: string;
  label: string;
  secondaryLabel?: string;
  description?: string;
}

export type SerpResultsOperator = NonNullable<Product['operator']>;

export interface SerpResultsDictionaries {
  operators: readonly SerpResultsOperator[];
  prepayments: readonly FirstPaymentDefinitionsDTO[];
}

export interface SerpDynamicFilterOptions {
  prepayments: readonly SerpFilterOption[];
  operators: readonly SerpFilterOption[];
  resorts: readonly SerpFilterOption[];
  directFlightAvailable: boolean;
  emiratesAvailable: boolean;
}

export interface SerpFilterTooltip {
  title: string;
  text: string;
}

export type SerpFilterControl = 'checkbox' | 'checkboxes' | 'chips' | 'radio';

interface SerpFilterGroupDecoration {
  component?: Component;
  appendIcon?: IconName;
  optionIcon?: IconName;
  optionIconColor?: string;
  tooltip?: SerpFilterTooltip;
}

export interface SerpCheckboxFilterGroup extends SerpFilterGroupDecoration {
  control: 'checkbox';
  search?: false;
  id: FilterGroupId.IsExclusive | FilterGroupId.BeachOwner | FilterGroupId.BonusMiles | FilterGroupId.NearTheCenter;
  title: string;
}

export interface SerpChipsFilterGroup extends SerpFilterGroupDecoration {
  control: 'chips';
  search?: false;
  id:
    | FilterGroupId.BeachLine
    | FilterGroupId.BeachType
    | FilterGroupId.Facilities
    | FilterGroupId.WaterActivities
    | FilterGroupId.ChildServices
    | FilterGroupId.Renovation
    | FilterGroupId.DistanceLift;
  title: string;
  options: SerpFilterOption[];
}

export interface SerpPrepaymentsFilterGroup extends SerpFilterGroupDecoration {
  control: 'chips';
  search?: false;
  id: FilterGroupId.Prepayments;
  title: string;
}

export interface SerpOperatorsFilterGroup extends SerpFilterGroupDecoration {
  control: 'checkboxes';
  search?: false;
  id: FilterGroupId.Operators;
  title: string;
}

export interface SerpRadioFilterGroup extends SerpFilterGroupDecoration {
  control: 'radio';
  search?: false;
  id: FilterGroupId.Rating | FilterGroupId.RestType;
  title: string;
  resetLabel: string;
  options: SerpFilterOption[];
}

export interface SerpBudgetFilterGroup {
  control: 'budget';
  search?: false;
  id: FilterGroupId.Budget;
  title: string;
  component: Component;
}

export type SerpOptionsFilterGroup =
  | SerpCheckboxFilterGroup
  | SerpChipsFilterGroup
  | SerpPrepaymentsFilterGroup
  | SerpOperatorsFilterGroup
  | SerpRadioFilterGroup;

export interface SerpSearchFilterGroup extends SerpFilterGroupDecoration {
  control: 'chips';
  search: true;
  id: SearchCriteriaFieldGroupId;
  title: string;
  options: SerpFilterOption[];
}

export interface SerpResortsFilterGroup extends SerpFilterGroupDecoration {
  control: 'checkboxes';
  search: true;
  id: ResortsFilterGroupId;
  title: string;
}

export interface SerpSearchCheckboxFilterGroup extends SerpFilterGroupDecoration {
  control: 'checkbox';
  search: true;
  id: SearchCriteriaFlagGroupId;
  title: string;
}

export type SerpCriteriaFilterGroup = SerpSearchFilterGroup | SerpResortsFilterGroup | SerpSearchCheckboxFilterGroup;

export type SerpFilterGroup = SerpOptionsFilterGroup | SerpBudgetFilterGroup | SerpCriteriaFilterGroup;

export interface SerpFilterOptionView {
  key: string;
  label: string;
  secondaryLabel?: string;
  description?: string;
  selected: boolean;
  disabled: boolean;
}

export interface SerpOptionsFilterGroupView {
  id: SerpFilterGroupId;
  control: SerpFilterControl;
  title: string;
  component?: Component;
  resetLabel?: string;
  appendIcon?: IconName;
  optionIcon?: IconName;
  optionIconColor?: string;
  tooltip?: SerpFilterTooltip;
  options: SerpFilterOptionView[];
}

export interface SerpBudgetPresetView {
  value: number;
  disabled: boolean;
}

export interface SerpBudgetFilterGroupView {
  id: FilterGroupId.Budget;
  control: 'budget';
  title: string;
  component: Component;
  minPrice: number;
  maxPrice: number;
  touristsCount: number;
  presets: SerpBudgetPresetView[];
}

export type SerpFilterGroupView = SerpOptionsFilterGroupView | SerpBudgetFilterGroupView;

export interface SelectedFilter {
  groupId: SerpFilterGroupId;
  key: string;
  label: string;
}

export interface SerpQuickFilter {
  id: SerpFilterGroupId;
  title?: string;
}

export interface SerpQuickFilterView {
  id: SerpFilterGroupId;
  control: SerpFilterGroup['control'];
  label: string;
  selected: boolean;
  available: boolean;
}

export interface SerpFilterViewContext {
  products: readonly SerpProduct[];
  filtersModel: FiltersModel;
  dynamicOptions: SerpDynamicFilterOptions;
}

export interface SerpPageProps {
  modelValue?: SearchFormCriteria | null;
  user?: AccountUser | null;
  accountUrl?: string;
  prices?: DestinationPrices;
  isVisibleRoomButton?: boolean;
  departureDisabled?: boolean;
  destinationDisabled?: boolean;
  mobile?: boolean;
}

export interface SerpFilterInteraction {
  groupId: SerpFilterGroupId;
  key: string | null;
  selected: boolean;
}

export type SeoTitleTag = 'h1' | 'h2';

export interface SeoTab {
  label: string;
  href: string;
  icon: IconName;
  active?: boolean;
}

export interface SeoMenuLink {
  label: string;
  href: string;
  active?: boolean;
}

/** Пункт с `items` не ссылка, а выпадашка по наведению. */
export interface SeoMenuItem {
  label: string;
  href?: string;
  active?: boolean;
  items?: SeoMenuLink[];
}

export type SeoDepartureCity = Pick<DepartureCity, 'id' | 'name'>;

/** Календарь низких цен. */
export interface SeoDeparture {
  label: string;
  placeholder: string;
  city: SeoDepartureCity | null;
  cities: SeoDepartureCity[];
}
